import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ColumnAccessorKey, ColumnConfiguration} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import ProcedurePageLinkForProcedureRun from 'procedures/ProcedurePageLinkForProcedureRun';
import * as ProcessManagementContextMenu from 'processes/dashboard/ProcessManagementContextMenu';
import ProcessTaskCellTitle from 'processes/dashboard/ProcessTaskCellTitle';
import {ProcessTaskStatusDisplay} from 'processes/dashboard/ProcessTaskDashboardDisplay';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  getStudiesRowContentHeight,
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ProcessTaskListingGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {
  ProcedureListingRenderer,
  ProcedureListingRendererType,
  getProcedureColumnData,
  ProcedureColumnKey,
} from './ProcedureListingConstants';

interface ProcedureRunTaskListingRendererExtras extends StudyableItemListingExtras {}

export default abstract class ProcedureRunTaskListingRenderer<
  T extends ProcedureListingRendererType,
> extends ProcedureListingRenderer<T> {
  getNonPluginColumnConfigurations(
    items: Array<ProcessTaskListingGraphQL>,
    {itemStudyAttributes}: ProcedureRunTaskListingRendererExtras = {}
  ): Array<ColumnConfiguration<ProcessTaskListingGraphQL>> {
    const maybeStudiesColumn = maybeGetStudiesListingColumn<ProcessTaskListingGraphQL>(itemStudyAttributes);

    return [
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          width: 200,
        },
        render: (item) => item.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.PROCESS_TASK_TYPE,
          header: 'Task type',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_PROCESS_TASK_TYPE,
        },
        render: (item) => processExecutionTypeDisplayName(this.clientOrigin, item.executionType),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.STATUS,
          header: getColumnHeaderFromSortKey(SortKey.SORT_STATUS),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_STATUS,
        },
        render: (item) => <ProcessTaskStatusDisplay status={item.status} />,
      },
      {
        column: {
          ...getProcedureColumnData(ProcedureColumnKey.PROCEDURE_TEMPLATE, this.clientOrigin),
          flexGrow: 1,
        },
        render: (item) => {
          const procedureRun = item.procedureRun;
          return procedureRun != null ? (
            <ProcedurePageLinkForProcedureRun procedureRun={procedureRun} />
          ) : null;
        },
      },
      ...(maybeStudiesColumn ? [maybeStudiesColumn] : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
        },
        render: (item) => <ProcessTaskCellTitle processTask={item} title={item.humanId} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
    ];
  }

  getNonPluginRowContentHeight(
    item: ProcessTaskListingGraphQL,
    {itemStudyAttributes}: ProcedureRunTaskListingRendererExtras
  ): number {
    return Math.max(
      ExpandedTableTagUtil.getRowHeight(item.fields),
      getStudiesRowContentHeight(item.id, itemStudyAttributes)
    );
  }

  openContextMenu(item: ProcessTaskListingGraphQL, e: BasicEvent, extras: unknown): void {
    ProcessManagementContextMenu.open(e.nativeEvent, ModelTypeConstants.PROCESS_TASK, item);
  }
}
